/**
 * Pure geo-math helpers for the AxisLink dual-party live map (threat pair proximity view).
 * Stateless functions on purpose: no DI/service registration required to use them.
 */

export interface GeoPoint {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_METERS = 6371000;
const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const METERS_PER_DEGREE_LAT = 111320;

/** How far the arrow is drawn per meter of per-step movement, in screen pixels. */
const ARROW_PX_PER_STEP_METER = 3;
/** Below this much movement per step the heading is noise, so no arrow is drawn. */
const ARROW_MIN_STEP_METERS = 0.1;
/** Default on-screen shaft length floor / cap (px) — keeps arrows readable and glanceable. */
const ARROW_MIN_LENGTH_PX = 8;
const ARROW_MAX_LENGTH_PX = 24;
/** Narrower angle → sharper tip; barbs sit closer to the shaft. */
const ARROW_BARB_ANGLE_RAD = (16 * Math.PI) / 180;
/** Barb length as a fraction of shaft length — long enough to read as a clear V. */
const ARROW_BARB_FRACTION = 0.24;
/**
 * Pull barbs slightly back from the tip along the shaft so the tip is a true point
 * rather than a blunt join under thick stroke.
 */
const ARROW_BARB_INSET_FRACTION = 0.08;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/** Great-circle distance between two lat/lng points, in meters (haversine formula). */
export function haversineDistanceMeters(from: GeoPoint, to: GeoPoint): number {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const a =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/** Initial compass bearing (0-360, 0 = North) travelling from `from` to `to`. */
export function bearingDegrees(from: GeoPoint, to: GeoPoint): number {
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const dLng = toRadians(to.lng - from.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}

/** Nearest 8-point compass label (N, NE, E, SE, S, SW, W, NW) for a bearing. */
export function compassDirection(bearing: number): string {
  const normalized = ((bearing % 360) + 360) % 360;
  const index = Math.round(normalized / 45) % COMPASS_POINTS.length;
  return COMPASS_POINTS[index];
}

/**
 * Web Mercator ground resolution: meters represented by one screen pixel at `lat` / `zoom`.
 * Used to convert a desired on-screen arrow length (px) into a geographic cap (m) so FR-402
 * velocity arrows stay glanceably sized across zoom levels.
 */
export function metersPerPixelAt(lat: number, zoom: number): number {
  return (
    (156543.03392 * Math.cos(toRadians(lat))) / Math.pow(2, zoom)
  );
}

/**
 * Inverse of `haversineDistanceMeters`/`bearingDegrees`: the point reached by travelling
 * `distanceMeters` from `from` along `bearingDeg` (0-360, 0 = North) along a great circle.
 * Used to synthesize plausible GPS points (e.g. mock live-location data) around a reference point.
 */
export function destinationPoint(
  from: GeoPoint,
  distanceMeters: number,
  bearingDeg: number,
): GeoPoint {
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;
  const bearing = toRadians(bearingDeg);
  const lat1 = toRadians(from.lat);
  const lng1 = toRadians(from.lng);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2),
    );

  return { lat: toDegrees(lat2), lng: toDegrees(lng2) };
}

/**
 * Velocity arrow (BFRD FR-402) as a single polyline path — shaft out to the tip, then back and
 * out to each barb: `[origin, tip, leftBarb, tip, rightBarb]`.
 *
 * Direction AND speed: the arrow points along recent travel and its on-screen length grows with
 * the distance covered per fix, so an operator reads "which way" and "how fast" from one mark.
 * Length is computed in **screen pixels** from step size, then converted to meters via
 * `metersPerPixel` so zoom in/out does not change how long the arrow looks next to fixed-pixel
 * markers. The step is averaged over the last two intervals when three fixes are available, so a
 * single noisy GPS sample can't swing it. Returns `null` when the party has effectively not
 * moved, which is FR-402's "suppressed rather than shown as arbitrary".
 *
 * Ported from the client's `nightweb-pair-monitor` reference widget (`arrowPath`).
 */
export function velocityArrowPath(
  track: GeoPoint[],
  metersPerPixel: number,
  maxLengthPx: number = ARROW_MAX_LENGTH_PX,
): GeoPoint[] | null {
  if (!track || track.length < 2) {
    return null;
  }
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) {
    return null;
  }

  const end = track[track.length - 1];
  const hasThree = track.length >= 3;
  const start = hasThree ? track[track.length - 3] : track[track.length - 2];
  const steps = hasThree ? 2 : 1;

  const metersPerDegreeLng =
    METERS_PER_DEGREE_LAT * Math.cos(toRadians(end.lat));
  if (Math.abs(metersPerDegreeLng) < 1) {
    return null; // effectively at a pole; the local flat-earth approximation breaks down
  }

  const east = ((end.lng - start.lng) / steps) * metersPerDegreeLng;
  const north = ((end.lat - start.lat) / steps) * METERS_PER_DEGREE_LAT;
  const stepMeters = Math.hypot(east, north);
  if (stepMeters < ARROW_MIN_STEP_METERS) {
    return null;
  }

  // Pixel length from speed, clamped — independent of zoom. Convert to meters only for drawing.
  const lengthPx = Math.min(
    maxLengthPx,
    Math.max(ARROW_MIN_LENGTH_PX, stepMeters * ARROW_PX_PER_STEP_METER),
  );
  const lengthMeters = lengthPx * metersPerPixel;
  const scale = lengthMeters / stepMeters;
  const tipEast = east * scale;
  const tipNorth = north * scale;
  const length = lengthMeters;

  const backEast = -tipEast / length;
  const backNorth = -tipNorth / length;
  const barb = length * ARROW_BARB_FRACTION;
  const inset = length * ARROW_BARB_INSET_FRACTION;
  const cos = Math.cos(ARROW_BARB_ANGLE_RAD);
  const sin = Math.sin(ARROW_BARB_ANGLE_RAD);

  const offset = (metersEast: number, metersNorth: number): GeoPoint => ({
    lat: end.lat + metersNorth / METERS_PER_DEGREE_LAT,
    lng: end.lng + metersEast / metersPerDegreeLng,
  });

  const tip = offset(tipEast, tipNorth);
  // Barb roots sit slightly behind the tip so thick polyline stroke doesn't blunt the point.
  const barbRootEast = tipEast + backEast * inset;
  const barbRootNorth = tipNorth + backNorth * inset;
  const leftEast = (backEast * cos - backNorth * sin) * barb;
  const leftNorth = (backEast * sin + backNorth * cos) * barb;
  const rightEast = (backEast * cos + backNorth * sin) * barb;
  const rightNorth = (-backEast * sin + backNorth * cos) * barb;

  return [
    { lat: end.lat, lng: end.lng },
    tip,
    offset(barbRootEast + leftEast, barbRootNorth + leftNorth),
    tip,
    offset(barbRootEast + rightEast, barbRootNorth + rightNorth),
  ];
}

export function getDistanceParameterData(distance: number, distanceParameter: string): number {
  if (distanceParameter === 'feet') {
    return Number((distance * 3.28084).toFixed(2));
  } else if (distanceParameter === 'yard') {
    return Number((distance * 1.09361).toFixed(2));
  } else if (distanceParameter === 'mile') {
    return Number((distance / 1609.34).toFixed(2));
  } else {
    return Number(distance.toFixed(2));
  }
}

export function getDistanceToMeter(distance: number, distanceParameter: string): number {
  if (distanceParameter === 'feet') {
    return Number((distance * 0.3048).toFixed(2));
  } else if (distanceParameter === 'yard') {
    return Number((distance * 0.9144).toFixed(2));
  } else if (distanceParameter === 'mile') {
    return Number((distance * 1609.34).toFixed(2));
  } else {
    return Number(distance.toFixed(2));
  }
}

export function getDistanceParameterLabel(distanceParameter: string): string {
  if (distanceParameter === 'feet') {
    return 'ft';
  } else if (distanceParameter === 'yard') {
    return 'yd';
  } else if (distanceParameter === 'mile') {
    return 'mi';
  } else {
    return 'm';
  }
}